import React, { useState, useEffect, useRef, useMemo } from "react";
import Chart from "chart.js/auto";
import { base_url } from "../../Global";

const FeedChart = () => {
  const [feeds, setFeeds] = useState([]);

  // Use refs instead of state for DOM elements and Chart instance
  const canvasRef = useRef(null);
  const chartInstanceRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const accessToken = localStorage.getItem("accessToken");
        if (!accessToken) {
          throw new Error("Access token not found in local storage");
        }
        const response = await fetch(base_url + "/entity/feed", {
          headers: {
            "Content-Type": "application/json",
            authorization: accessToken,
          },
        });
        const feedData = await response.json();
        setFeeds(feedData?.allMedia || []);
      } catch (error) {
        console.error("Error fetching feed data:", error);
      }
    };

    fetchData();
  }, []);

  // useMemo ensures we only calculate chart data when 'feeds' actually changes
  const { monthlyCounts, monthLabels, totalFeeds } = useMemo(() => {
    if (!feeds.length)
      return { monthlyCounts: [], monthLabels: [], totalFeeds: 0 };

    const currentDate = new Date();
    const counts = Array(6).fill(0);
    const labels = Array(6).fill("");

    for (let i = 0; i < 6; i++) {
      const targetDate = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() - i,
        1,
      );
      labels[5 - i] = targetDate.toLocaleString("default", { month: "long" });
    }

    feeds.forEach((feed) => {
      const createdAtDate = new Date(feed.lastUpdatedAt);
      const diffMonths =
        (currentDate.getFullYear() - createdAtDate.getFullYear()) * 12 +
        (currentDate.getMonth() - createdAtDate.getMonth());

      if (diffMonths >= 0 && diffMonths < 6) {
        counts[5 - diffMonths]++;
      }
    });

    return {
      monthlyCounts: counts,
      monthLabels: labels,
      totalFeeds: feeds.length,
    };
  }, [feeds]);

  useEffect(() => {
    if (!canvasRef.current || monthlyCounts.length === 0) return;

    if (chartInstanceRef.current) {
      chartInstanceRef.current.destroy();
    }

    const ctx = canvasRef.current.getContext("2d");

    chartInstanceRef.current = new Chart(ctx, {
      type: "bar",
      data: {
        labels: monthLabels,
        datasets: [
          {
            label: "Feeds",
            data: monthlyCounts,
            backgroundColor: "rgba(54, 162, 235, 0.5)",
            borderColor: "rgba(54, 162, 235, 1)",
            borderWidth: 1,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: true, // Helps keep height consistent with UserChart
        scales: {
          y: {
            beginAtZero: true,
            title: {
              display: true,
              text: "Number of Feeds",
            },
          },
          x: {
            title: {
              display: true,
              text: "Month",
            },
          },
        },
      },
    });

    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
      }
    };
  }, [monthlyCounts, monthLabels]);

  // If there's no data, don't render anything
  if (totalFeeds === 0) return null;

  return (
    // Copied the exact container classes from UserChart to ensure identical sizing
    <div className="flex-1 w-full rounded-[18.28px] mt-5 bg-gray-100 shadow-[0px_0px_13.71px_rgba(0,_0,_0,_0.15)] flex flex-col items-start justify-start pt-[1.5rem] pb-[1.188rem] pr-[0.813rem] pl-[1rem] box-border relative gap-[2.688rem_0rem] min-w-[20.188rem] max-w-full z-[1] mq675:gap-[2.688rem_0rem]">
      <div className="w-full px-2 d-flex justify-content-between">
        <h2 className="mr-">Feeds</h2>
        <h2>{totalFeeds}</h2>
      </div>
      <canvas ref={canvasRef} width="400" height="200"></canvas>
    </div>
  );
};

export default FeedChart;
